import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import CommonProps from '../CommonProps';
import series from '../utils/Series';
import AnimatedDotSeries from './AnimatedDotSeries';
import AnimatedLinePath from './AnimatedLinePath';

import styles from './LineDot.css';

function LineDot(props) {
  const { xScaleSet, yScaleSet, tension, lineClassName, bars, data, dotClassName, animateChart } =
    props;
  const { locale } = useIntl();

  const renderOneBarSeries = useCallback(
    (b, idx) => (
      <rect
        x={xScaleSet(b.x) - 5}
        y={b.y}
        height={b.height}
        width={10}
        style={{ fill: '#54546b', opacity: 1 - idx * 0.2 }}
      />
    ),
    [xScaleSet],
  );

  const renderOneBar = useCallback(bar => bar.map(renderOneBarSeries), [renderOneBarSeries]);

  const rowHasData = useCallback((row, seriesField) => {
    if (!row) {
      return false;
    }
    if (Object.prototype.hasOwnProperty.call(row, 'total')) {
      return row.total > 0;
    }
    const bucket = row[seriesField];
    if (bucket && Object.prototype.hasOwnProperty.call(bucket, 'total')) {
      return bucket.total > 0;
    }
    return true;
  }, []);

  const dataset = series(props);

  const renderOneSeries = line => {
    const linePoints = line.data.filter(
      (pt, i) => pt.y != null && !Number.isNaN(+pt.y) && rowHasData(data[i], line.field),
    );
    const dots = line.data
      .map((v, i) => ({ ...v, i }))
      .filter((_, i) => rowHasData(data[i], line.field));

    return (
      <g key={line.field}>
        {bars ? bars.map(renderOneBar) : null}
        <AnimatedLinePath
          points={linePoints}
          xScaleSet={xScaleSet}
          yScaleSet={yScaleSet}
          tension={tension}
          animate={animateChart}
          className={lineClassName}
          stroke={line.color}
          strokeWidth={typeof line.strokeWidth === 'number' ? line.strokeWidth : 2}
          strokeDasharray={line.strokeDasharray}
          strokeLinecap={line.strokeLinecap || 'round'}
          style={line.style}
        />
        {line.symbol === 'circle' || !line.hideLabels ? (
          <AnimatedDotSeries
            dots={dots}
            line={line}
            data={data}
            xScaleSet={xScaleSet}
            yScaleSet={yScaleSet}
            animate={animateChart}
            dotClassName={dotClassName}
            locale={locale}
            dotGroupClassName={styles.dotGroup}
            dotClassNameStyles={styles.dot}
            dotEmptyClassName={styles.dotEmpty}
            labelClassName={styles.label}
          />
        ) : null}
      </g>
    );
  };

  return <g className={styles.lineGroup}>{dataset.map(renderOneSeries)}</g>;
}

LineDot.propTypes = {
  lineClassName: PropTypes.string,
  dotClassName: PropTypes.string,
  xScaleSet: PropTypes.func.isRequired,
  yScaleSet: PropTypes.func.isRequired,
  bars: PropTypes.any,
  data: PropTypes.array.isRequired,
  chartPeriod: PropTypes.string.isRequired,
  tension: PropTypes.number,
  animateChart: PropTypes.bool,
};

LineDot.defaultProps = {
  interpolate: null,
  lineClassName: 'react-d3-basic__line',
  dotClassName: 'react-d3-basic__dot',
  ...CommonProps,
  bars: null,
  tension: 0.85,
};

export default LineDot;
